import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface SuccessProps {
  message?: string;
  onClose?: () => void;
}

interface ErrorProps {
  message?: string;
  onRetry?: () => void;
  onClose?: () => void;
}

interface DataFetchErrorProps {
  onRetry?: () => void;
  onClose?: () => void;
}

const Success: React.FC<SuccessProps> = ({ message = "Claim updated successfully", onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Success</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">{message}</p>
          </div>

          {/* Dashboard Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.claimsDashboard.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Back to Dashboard
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ message = "Failed to update claim", onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">An Error Occurred</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 text-center">{message}</p>

          {/* Footer Buttons */}
          <div className="flex justify-center gap-2.5 pt-2">
            <Link to={ROUTES_CONFIG.claimsDashboard.path}>
              <button className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center">
                Back to Dashboard
              </button>
            </Link>
            {onRetry && (
              <button
                className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DataFetchError: React.FC<DataFetchErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Data Fetch Error</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 text-center">Failed to load claim data. Please try again.</p>

          {/* Footer Buttons */}
          <div className="flex justify-center gap-2.5 pt-2">
            <Link to={ROUTES_CONFIG.claimsDashboard.path}>
              <button className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center">
                Back to Dashboard
              </button>
            </Link>
            {onRetry && (
              <button
                className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
                onClick={onRetry}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface CannotEditProps {
  claimId: string;
  claimStatus: string;
  onClose?: () => void;
}

const CannotEdit: React.FC<CannotEditProps> = ({ claimId, claimStatus, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Cannot Edit Claim</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-700 text-center">
            This claim cannot be edited because it is not in "Pending" status. Current status:{" "}
            <strong>{claimStatus}</strong>
          </p>

          {/* Footer Buttons */}
          <div className="flex justify-center gap-2.5 pt-2">
            <Link to={`${ROUTES_CONFIG.claim.path}?id=${claimId}`}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                View Claim Details
              </button>
            </Link>
            <Link to={ROUTES_CONFIG.claimsDashboard.path}>
              <button className="text-black border border-black bg-white text-sm font-normal py-1.5 px-0 transition-all duration-300 rounded-[32px] w-[200px] min-w-[50px] hover:cursor-pointer disabled:cursor-auto disabled:text-gray-300 text-center">
                Back to Dashboard
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditClaimModals = {
  Success,
  Error,
  DataFetchError,
  CannotEdit
};

export { EditClaimModals };
